import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const StarIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#CE2B37" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
);

const Reviews = () => {
    const [reviews, setReviews] = useState([]);
    const [current, setCurrent] = useState(0);
    const [expanded, setExpanded] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(0);
    const titleRef = useRef(null);
    const autoRef = useRef(null);

    useEffect(() => {
        axios.get('https://kyrgyz-adventure-travel.com/api/v1/tours/reviews')
            .then(r => setReviews(r.data.results))
            .catch(e => console.error(e));
    }, []);

    // Intersection observer for title
    useEffect(() => {
        const observer = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) e.target.classList.add('visible');
        }, { threshold: 0.3 });
        if (titleRef.current) observer.observe(titleRef.current);
        return () => observer.disconnect();
    }, []);

    const VISIBLE = 2;
    const maxIdx = Math.max(0, reviews.length - VISIBLE);

    const prev = useCallback(() => setCurrent(c => Math.max(0, c - 1)), []);
    const next = useCallback(() => setCurrent(c => Math.min(maxIdx, c + 1)), [maxIdx]);

    // Auto-advance
    useEffect(() => {
        if (reviews.length === 0) return;
        autoRef.current = setInterval(() => {
            setCurrent(c => (c >= maxIdx ? 0 : c + 1));
        }, 4000);
        return () => clearInterval(autoRef.current);
    }, [reviews, maxIdx]);

    const resetAuto = () => {
        clearInterval(autoRef.current);
        autoRef.current = setInterval(() => {
            setCurrent(c => (c >= maxIdx ? 0 : c + 1));
        }, 4000);
    };

    const handlePrev = () => { prev(); resetAuto(); };
    const handleNext = () => { next(); resetAuto(); };

    const onDragStart = (x) => { setIsDragging(true); setDragStart(x); };
    const onDragEnd   = (x) => {
        if (!isDragging) return;
        setIsDragging(false);
        const diff = dragStart - x;
        if (Math.abs(diff) > 50) { diff > 0 ? handleNext() : handlePrev(); }
    };

    const cardWidth = 100 / VISIBLE;

    return (
        <div className="bg-dark-2 py-24 md:py-36 overflow-hidden">
            <div className="container">
                {/* Header */}
                <div ref={titleRef} className="reveal flex flex-col items-center gap-4 mb-16 text-center">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-px bg-it-green opacity-90" />
                        <p className="font-unbounded text-white text-xs uppercase tracking-widest">What they say about us</p>
                        <div className="w-8 h-px bg-gold opacity-70" />
                    </div>
                    <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white">Reviews</h2>
                </div>

                {/* Carousel */}
                <div
                    className="overflow-hidden rounded-3xl"
                    onMouseDown={e => onDragStart(e.clientX)}
                    onMouseUp={e => onDragEnd(e.clientX)}
                    onTouchStart={e => onDragStart(e.touches[0].clientX)}
                    onTouchEnd={e => onDragEnd(e.changedTouches[0].clientX)}
                >
                    <div
                        className="carousel-track"
                        style={{ transform: `translateX(-${current * (cardWidth + 2)}%)` }}
                    >
                        {reviews.map((review, i) => (
                            <div
                                key={i}
                                className="flex-shrink-0 group"
                                style={{ width: `calc(${cardWidth}% - 12px)` }}
                            >
                                <div className="adv-card p-7 flex flex-col gap-5 h-full">
                                    <div className="flex gap-1">
                                        {[...Array(5)].map((_, j) => <StarIcon key={j} />)}
                                    </div>

                                    <p
                                        className="font-manrope text-white/65 text-sm leading-relaxed cursor-pointer hover:text-white/85 transition-colors duration-200 flex-1"
                                        onClick={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                                    >
                                        {expanded[i]
                                            ? review.text
                                            : review.text.length > 160
                                                ? `${review.text.substring(0, 160)}...`
                                                : review.text}
                                    </p>

                                    {review.text.length > 160 && (
                                        <button
                                            onClick={() => setExpanded(prev => ({ ...prev, [i]: !prev[i] }))}
                                            className="font-unbounded text-gold/60 text-[10px] uppercase tracking-wider hover:text-gold transition-colors self-start"
                                        >
                                            {expanded[i] ? 'Show less ↑' : 'Read more ↓'}
                                        </button>
                                    )}

                                    <div className="flex items-center gap-4 pt-4 border-t border-green-border/40">
                                        <div
                                            className="w-12 h-12 rounded-2xl bg-cover bg-center flex-shrink-0 ring-2 ring-transparent group-hover:ring-gold/30 transition-all duration-300"
                                            style={{ backgroundImage: `url(${review.file})` }}
                                        />
                                        <div>
                                            <span className="font-unbounded font-semibold text-sm text-white block">{review.reviewer}</span>
                                            <span className="font-manrope text-white/40 text-xs">Verified customer</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Controls */}
                {reviews.length > VISIBLE && (
                    <div className="flex items-center justify-center gap-6 mt-10">
                        <button className="carousel-btn" onClick={handlePrev} disabled={current === 0}
                                style={{ opacity: current === 0 ? 0.3 : 1 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6"/>
                            </svg>
                        </button>

                        {/* Dots */}
                        <div className="flex gap-2">
                            {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => { setCurrent(i); resetAuto(); }}
                                    className="rounded-full transition-all duration-300"
                                    style={{
                                        width: i === current ? '24px' : '8px',
                                        height: '8px',
                                        background: i === current ? '#CE2B37' : 'rgba(255,255,255,0.2)',
                                    }}
                                />
                            ))}
                        </div>

                        <button className="carousel-btn" onClick={handleNext} disabled={current === maxIdx}
                                style={{ opacity: current === maxIdx ? 0.3 : 1 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reviews;
